import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import useQueryParam from 'shared/Hooks/useQueryParam'
import { AppState } from 'redux/config/store'
import Banner from 'core-system/Banner'
import Loading from 'core-system/Loading'
import { OtpGroup, OtpProgram } from 'redux/otp/otpTypes'
import ProgramHeader from 'core-system/Program/ProgramHeader'
import OTPPreviewModal from './OTPPreviewModal'
import { getClientEnvironment } from 'redux/config/services/Constants'
import moment from 'moment'
import OTPOverviewView from './OTPOverviewView'
import { ProgramMissingPaymentMethod } from 'core-system/Program'
import NoPaymentModal from 'shared/components/NoPaymentModal'

const OTPView = React.memo(() => {
  const segmentId = useQueryParam('segmentId')
  const navigate = useNavigate()

  const otpProgramsLoaded = useSelector(
    (state: AppState) => state.otp.otpProgramsLoaded
  )
  const otpPrograms = useSelector((state: AppState) => state.otp.otpPrograms)
  const {
    segmentsMap,
    profile,
    hasValidPaymentMethod,
    hasValidDistributionType,
  } = useSelector((state: AppState) => state.employer)

  const [otpGroup, setOtpGroup] = useState<OtpGroup>(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isNoPaymentModalOpen, setIsNoPaymentModalOpen] = useState(false)

  const handleGetStartedPress = () => {
    if (hasValidPaymentMethod || !profile?.openRegistration) {
      navigate(`/one-time-purchase/${segmentId}/activation`)
    } else {
      setIsNoPaymentModalOpen(true)
    }
  }

  useEffect(() => {
    setOtpGroup({
      active: otpPrograms[segmentId] && otpPrograms[segmentId].active,
      nextMonth: otpPrograms[segmentId] && otpPrograms[segmentId].nextMonth,
    })
  }, [otpPrograms, segmentId])

  if (!otpProgramsLoaded || !profile || !otpGroup) {
    return <Loading fullPage />
  }

  if (
    otpPrograms &&
    (!otpGroup.active || otpGroup.active.segment !== segmentId) &&
    (!otpGroup.nextMonth || otpGroup.nextMonth.segment !== segmentId)
  ) {
    return (
      <>
        <ProgramHeader segmentId={segmentId} />
        <Banner
          variant='cta'
          title='Start your One Time Purchase Program today!'
          description={`Give ${
            segmentsMap[segmentId].name === 'All Employees'
              ? 'All Commuters'
              : segmentsMap[segmentId].name
          } ${
            segmentsMap[segmentId].name === 'All Employees' ? '' : 'commuters'
          } the ability to make a one time purchase to help with their commute.`}
          cta='Get Started'
          ctaCallback={() => handleGetStartedPress()}
          background='bannerGeneral'
        />
        <OTPPreviewModal
          open={isModalOpen}
          closeModal={() => setIsModalOpen(false)}
          segmentId={segmentId}
        />
        <NoPaymentModal
          open={isNoPaymentModalOpen}
          closeModal={() => setIsNoPaymentModalOpen(false)}
          product='otp'
        />
      </>
    )
  }

  const isValid = hasValidDistributionType && hasValidPaymentMethod
  const isNextMonth = !otpGroup.active && otpGroup.nextMonth
  const isCancelling =
    !otpGroup.active &&
    otpGroup.nextMonth &&
    otpGroup.nextMonth.status === 'CANCELLING'
  const isPending = isNextMonth && !isCancelling
  const isDevCheck =
    getClientEnvironment() === 'development' && !isPending && !isCancelling
  const isOverviewCheck = otpGroup.active && isValid

  return (
    <>
      <ProgramHeader
        segmentId={segmentId}
        manageRoute={
          isDevCheck || isOverviewCheck
            ? `/one-time-purchase/${segmentId}/manage`
            : null
        }
      />
      {isPending && (
        <Banner
          variant='cta'
          background='bannerGeneral'
          title='Starting Soon!'
          description={`Your One Time Purchase program is scheduled to start on ${moment(
            otpGroup.nextMonth.startDate
          ).format('MMMM Do, YYYY')}.`}
          cta='Manage Program'
          ctaCallback={() => navigate(`/one-time-purchase/${segmentId}/manage`)}
        />
      )}
      {isCancelling && (
        <Banner
          variant='cta'
          background='bannerGeneral'
          title='Program Cancelling'
          description='Your program cancellation request has been submitted. Our team will reach out soon.'
          cta='Manage Program'
          ctaCallback={() => navigate(`/one-time-purchase/${segmentId}/manage`)}
        />
      )}
      {isDevCheck ? (
        <OTPOverviewView activeProgram={otpGroup.active as OtpProgram} />
      ) : (
        <>
          {!isValid && (
            <ProgramMissingPaymentMethod
              from={`/one-time-purchase/${segmentId}`}
            />
          )}
          {isOverviewCheck && (
            <OTPOverviewView activeProgram={otpGroup.active as OtpProgram} />
          )}
        </>
      )}
    </>
  )
})

// Helps to identify component in React error logs
if (process.env.NODE_ENV !== 'production') {
  OTPView.displayName = 'OTPView'
}

export default OTPView
